// options.js

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'

export default {
    props: {
        campaignId: String,
        title: String,
        description: String,
        centerImage: {
            type: Array,
            default: () => []
        },
        nonCenteredImages: {
            type: Array,
            default: () => []
        },
        linkUrl: String,
        linkText: String,
        linkTitle: String,
        linkTarget: {
            type: String,
            default: '_self'
        }
    },

    setup(props) {
        const store = useStore()

        // ——— donation counter ———
        const fundraisingAmount = ref(1)
        const updateAmount = () => {
            // store.dispatch('fundraising/GetHighlightedDonations', {
            //     fundraisingCampaignId: props.campaignId
            // })
            // .then((sum) => (fundraisingAmount.value = sum))
            // .catch((err) => console.error('Fetch error:', err))
            fundraisingAmount.value = 3999
            // fundraisingAmount.value += getRandomInt(2000, 5000)
        }

        const digitGroups = computed(() => {
            const raw = fundraisingAmount.value.toString()
            const groups = raw.match(/\d{1,3}(?=(?:\d{3})*$)/g) || []
            const result = groups.map(g => g.padStart(3, '\u200B'))
            console.log(result)
            return result
        })

        function getRandomInt(min, max) {
            min = Math.ceil(min)
            max = Math.floor(max)
            return Math.floor(Math.random() * (max - min + 1)) + min
        }

        const width = ref(window.innerWidth)
        const onResize = () => (width.value = window.innerWidth)
        const imageKey = computed(() =>
            width.value > 800 ? 0 : width.value > 768 ? 1 : 2
        )

        let intervalId
        let initialTimeoutId

        onMounted(() => {
            window.addEventListener('resize', onResize)

            initialTimeoutId = setTimeout(() => {
                updateAmount()
                intervalId = setInterval(updateAmount, 1500)
            }, 500)
        })

        onUnmounted(() => {
            clearTimeout(initialTimeoutId)
            clearInterval(intervalId)
            window.removeEventListener('resize', onResize)
        })

        return {
            ...props,
            fundraisingAmount,
            digitGroups,
            width,
            imageKey
        }
    }
}


// rolling-number.js
// Sourced and modified from https://github.com/layflags/rolling-number

function htmlEscape(string) {
  return string
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function html(strings, ...args) {
  return strings
    .map((str, i) =>
      i < args.length
        ? str +
          (args[i].__html
            ? [].concat(args[i].__html).join('')
            : htmlEscape(String(args[i])))
        : str
    )
    .join('')
    .trim()
}

function toDigits(num, size = 0) {
  const result =
    (typeof num === 'string' || !Number.isNaN(num))
      ? num.toString().split('')
      : []
  const padSize = Math.max(0, size - result.length)
  return [...Array(padSize).fill('\u200B'), ...result]
}

function toSize(num) {
  return (typeof num === 'string' || !Number.isNaN(num))
    ? num.toString().length
    : 0
}

function renderStyles() {
  return html`
    <style>
      :host { --roll-duration: 1s; }
      .digit {
        width: 1ch;
        overflow: hidden;
        display: inline-flex;
        position: relative;
      }
      .value { color: transparent; position: relative; }
      .scale {
        user-select: none;
        position: absolute;
        left: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        transition: transform var(--roll-duration);
      }
      .scale span:last-child { position: absolute; bottom: -10%; left: 0; }
      [data-value="\u200B"] .scale { transform: translateY(10%); }
      [data-value="0"] .scale { transform: translateY(0); }
      [data-value="1"] .scale { transform: translateY(-10%); }
      [data-value="2"] .scale { transform: translateY(-20%); }
      [data-value="3"] .scale { transform: translateY(-30%); }
      [data-value="4"] .scale { transform: translateY(-40%); }
      [data-value="5"] .scale { transform: translateY(-50%); }
      [data-value="6"] .scale { transform: translateY(-60%); }
      [data-value="7"] .scale { transform: translateY(-70%); }
      [data-value="8"] .scale { transform: translateY(-80%); }
      [data-value="9"] .scale { transform: translateY(-90%); }
      [data-value="-"] .scale { transform: translateY(-100%); }
    </style>
  `
}

function renderDigit(value, index) {
  return html`
    <span class="digit" data-value="${value}" id="digit${index}">
      <span class="scale" aria-hidden="true">
        <span>0</span><span>1</span><span>2</span><span>3</span>
        <span>4</span><span>5</span><span>6</span><span>7</span>
        <span>8</span><span>9</span><span>&ZeroWidthSpace</span>
      </span>
      <span class="value">${value}</span>
    </span>
  `
}

function renderRoot() {
  return html`
    ${{ __html: renderStyles() }}
    <span id="wrapper"></span>
  `
}

function render($wrapper, nextState, prevState) {
  const newStr   = String(nextState.value)
  const newCount = newStr.length
  const oldStr   = String(prevState.value)
  const oldCount = prevState.size

  let placeholder

  if (oldCount === 0) {
    placeholder = Array(newCount).fill('0')
  } else if (newCount > oldCount) {
    placeholder = [
      ...Array(newCount - oldCount).fill('\u200B'),
      ...Array(oldCount).fill('0')
    ]
  } else {
    placeholder = toDigits(oldStr, newCount)
  }

  $wrapper.innerHTML = placeholder.map(renderDigit).join('')

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      newStr.split('').forEach((digit, i) => {
        const el = $wrapper.querySelector(`#digit${i}`)
        if (!el) return
        el.dataset.value = digit
        el.querySelector('.value').textContent = digit
      })
    })
  })
}

const INTERNAL = Symbol('INTERNAL')

class RollingNumber extends HTMLElement {
  static get observedAttributes() { return ['value'] }

  [INTERNAL] = {
    $wrapper: null,
    state:    { value: NaN, size: 0 },
    update:   (payload) => {
      if (!this[INTERNAL].$wrapper) return
      if ('value' in payload) {
        const { value } = payload
        const size      = toSize(value)
        const nextState = { value, size }
        render(this[INTERNAL].$wrapper, nextState, this[INTERNAL].state)
        this[INTERNAL].state = nextState
      }
    }
  }

  constructor() {
    super()
    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = renderRoot()
    this[INTERNAL].$wrapper = shadow.getElementById('wrapper')
  }

  get value() {
    return this[INTERNAL].state.value
  }

  set value(val) {
    this[INTERNAL].update({ value: val })
  }

  attributeChangedCallback(name, _, newVal) {
    if (name === 'value') {
      this.value = newVal
    }
  }

  connectedCallback() {
    // no initial update here; attributeChangedCallback handles it
  }
}

customElements.define('layflags-rolling-number', RollingNumber)
export { RollingNumber }
